import { Badge } from '@mantine/core';
import { IconBell } from '@tabler/icons-react';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';

import {
  BatchTypeEnum,
  Declaration,
  DeclarationCustomsFlowType,
  DeclarationCustomsType,
  DeclarationStatus,
  EORIGIN_DATE_FORMAT_WITH_TIME,
} from '@e-origin/shared';

import { copyIcon, dangerIcon, IconFactory, sendIcon, viewIcon } from '@assets';
import { TableActions, TableColumnProps, TableNotificationIndicator } from '@components';
import { DeclarationFlowBackgroundColors } from '@enums';
import { useSendBatchToBeGate } from '@hooks/batch';
import { useDeleteDeclaration, useSendDeclarationsByLrn, useSendOneHv, useSendOneLv } from '@hooks/declaration';
import { useSettings } from '@hooks/useSettings';
import { isHighValueDeclaration, toDecimal } from '@utils';
import { countCustomsStateNotifications, getDuplicationModalTitle } from '../../../helpers';
import { declarationModalManager } from '../../declarationModalManager';
import { DeclarationStatusIndicator } from './DeclarationStatusIndicator';

// eslint-disable-next-line max-statements
export const useDeclarationTableColumns = ({ onSelectedRow, filters, batchId, listTab, lrn, batchView, comments }) => {
  const navigate = useNavigate();
  const sendAllDeclarationsWithSameLRNMutation = useSendDeclarationsByLrn();
  const sendOneHighValueMutation = useSendOneHv();
  const sendOneLowValueMutation = useSendOneLv();
  const sendBatchToBeGateMutation = useSendBatchToBeGate();
  const deleteDeclarationMutation = useDeleteDeclaration();

  const { data: settings } = useSettings();

  const highValuesActions = (row: Declaration) => {
    if (!isHighValueDeclaration(row.generalInfo.group)) {
      return [];
    }

    return [
      {
        icon: copyIcon,
        text: getDuplicationModalTitle(row),
        onClick: () => declarationModalManager.openDuplicateAndAmendment(row),
        disableAfterClick: false,
      },
      {
        if: row.generalInfo.status === DeclarationStatus.ACCEPTED,
        icon: dangerIcon,
        text: 'Move to customs control',
        onClick: () => declarationModalManager.openCustomsControl(row),
      },
      {
        if: (() => {
          if (
            row.generalInfo?.status === DeclarationStatus.REJECTED ||
            (row.generalInfo?.status === DeclarationStatus.ACCEPTED && row.customsState?.flow === DeclarationCustomsFlowType.PRE)
          ) {
            return true;
          }

          if (
            row.generalInfo?.status === DeclarationStatus.SENT &&
            Math.abs(moment.duration(moment().diff(moment(row.updatedAt))).asMinutes()) >= settings.reSendDelay
          ) {
            return true;
          }

          return false;
        })(),
        icon: sendIcon,
        text: 'Send',
        disableAfterClick: true,
        onClick: () => {
          if (
            [DeclarationCustomsType.IDMS, DeclarationCustomsType.AES, DeclarationCustomsType.DMS, DeclarationCustomsType.NL_AES].includes(
              row.customsState?.sentTo,
            ) &&
            !sendOneHighValueMutation.isPending
          ) {
            sendOneHighValueMutation.mutate({ lrn: row.messageInfo.LRN });
            return;
          }

          if (row.customsState?.sentTo === DeclarationCustomsType.BE_GATE && !sendBatchToBeGateMutation.isPending) {
            sendBatchToBeGateMutation.mutate({ batchId: row.messageInfo.batch, lrn: row.messageInfo.LRN });
            return;
          }

          if (!sendAllDeclarationsWithSameLRNMutation.isPending) {
            sendAllDeclarationsWithSameLRNMutation.mutate({ batchId: row.messageInfo.batch, movementReferenceNumber: '', lrns: [row.messageInfo.LRN] });
          }
        },
      },
      {
        // Informational, disabled entry while the LRN group is in SENDING.
        if: row.generalInfo?.status === DeclarationStatus.SENDING,
        icon: sendIcon,
        text: 'Sending in progress',
        disabled: true,
        onClick: () => undefined,
      },
    ];
  };

  const lowValuesActions = (row: Declaration) => {
    if (isHighValueDeclaration(row.generalInfo.group)) {
      return [];
    }

    return [
      {
        if: (() => {
          if (
            row.generalInfo?.status === DeclarationStatus.NOT_SENT ||
            row.generalInfo?.status === DeclarationStatus.REJECTED ||
            (row.generalInfo?.status === DeclarationStatus.ACCEPTED &&
              row.customsState?.flow === DeclarationCustomsFlowType.PRE &&
              !row.customsState?.invalidationRequest?.invalidationRequestDate)
          ) {
            return true;
          }

          if (
            row.generalInfo?.status === DeclarationStatus.SENT &&
            Math.abs(moment.duration(moment().diff(moment(row.updatedAt))).asMinutes()) >= settings?.reSendDelay
          ) {
            return true;
          }

          return false;
        })(),
        icon: sendIcon,
        text: 'Send',
        disableAfterClick: true,
        onClick: () => {
          if (!sendOneLowValueMutation.isPending) {
            sendOneLowValueMutation.mutate({ id: row._id });
          }
        },
      },
      {
        // Show an informational, disabled entry while the declaration is in
        // SENDING (outbound complete, waiting for the customs-events reducer
        // to apply the SENT ack). Keeps the affordance discoverable instead
        // of presenting an empty action cell.
        if: row.generalInfo?.status === DeclarationStatus.SENDING,
        icon: sendIcon,
        text: 'Sending in progress',
        disabled: true,
        onClick: () => undefined,
      },
      {
        if: row.generalInfo?.status === DeclarationStatus.REJECTED,
        icon: IconFactory.delete,
        text: 'Delete',
        onClick: () => declarationModalManager.openDeleteDeclaration({ declaration: row, onConfirm: () => deleteDeclarationMutation.mutate(row._id) }),
        disableAfterClick: false,
      },
    ];
  };

  const commonActions = (row: Declaration) => {
    return [
      {
        icon: viewIcon,
        text: 'View details',
        onClick: () => {
          navigate(`/declarations/${row._id}${listTab ? '?listTab=' + listTab : ''}${batchId ? '&batchId=' + batchId : ''}${lrn ? '&lrn=' + lrn : ''}`, {
            replace: true,
          });
        },
      },
    ];
  };

  const buildColumns = (): TableColumnProps<Declaration>[] => [
    ...([BatchTypeEnum.HIGH_VALUES, BatchTypeEnum.HIGH_VALUES_EXPORT].includes(batchView)
      ? [
          {
            name: <IconBell />,
            cell: (row) => {
              const { actionsPending, notificationsPending } = countCustomsStateNotifications(row);
              const hasComments = comments.filter((comment) => comment.entityId === row._id).length > 0;

              return <TableNotificationIndicator actionsPending={actionsPending} notificationsPending={notificationsPending} comments={hasComments} />;
            },
          },
        ]
      : []),
    {
      name: 'Checked',
      cell: (row) => <DeclarationStatusIndicator declaration={row} />,
    },
    {
      name: 'Batch ID',
      selector: 'messageInfo.batchId',
      cell: (row, rowIdx) => <div data-pw={`declarations-row-batch-id-${rowIdx}`}>{row.messageInfo.batchId}</div>,
      sortable: true,
    },
    ...(filters.group === 'H1'
      ? [
          {
            name: 'Flow/System',
            cell: (row) => (
              <div className="whitespace-nowrap overflow-hidden overflow-ellipsis">
                {row.customsState?.sentTo && (
                  <Badge size="lg" color={DeclarationFlowBackgroundColors[row.customsState?.flow ?? 'POST']}>
                    {row.customsState.flow?.toLowerCase() || ''}
                    {row.customsState.sentTo}
                  </Badge>
                )}
              </div>
            ),
          },
          {
            name: 'HS Code',
            cell: (row: Declaration) => (
              <div className="whitespace-nowrap overflow-hidden overflow-ellipsis">
                {row.goodShipmentGlobal?.firstItemData?.hsCode?.toString().length ? row.goodShipmentGlobal?.firstItemData?.hsCode : 'N/A'}
              </div>
            ),
          },
          {
            name: 'Description',
            cell: (row: Declaration) => (
              <div className="whitespace-nowrap overflow-hidden overflow-ellipsis">
                {row.goodShipmentGlobal?.firstItemData?.invoiceDescription?.length ? row.goodShipmentGlobal?.firstItemData?.invoiceDescription : 'N/A'}
              </div>
            ),
          },
          {
            name: 'Sequence Number',
            selector: 'goodShipmentGlobal.sequence',
            cell: (row: Declaration) => <div className="whitespace-nowrap overflow-hidden overflow-ellipsis">{row.goodShipmentGlobal?.sequence || ''}</div>,
            sortable: true,
          },
          {
            name: 'Total amount invoiced',
            cell: (row) => (
              <div className="whitespace-nowrap overflow-hidden overflow-ellipsis">{toDecimal(row.goodShipmentGlobal?.totalAmountInvoiced) || 'N/A'}</div>
            ),
          },
          {
            name: 'Statistical value',
            cell: (row: Declaration) => (
              <div className="whitespace-nowrap overflow-hidden overflow-ellipsis">{row.otherDataElements?.statisticalValue || 'N/A'}</div>
            ),
          },
        ]
      : []),
    {
      name: 'Number of items',
      cell: (row) => <div className="whitespace-nowrap overflow-hidden overflow-ellipsis">{row.virtuals?.goodsShipmentItemsLength || 'N/A'}</div>,
    },
    ...(filters.group !== 'H1'
      ? [
          {
            name: 'Flow/System',
            cell: (row) => (
              <div className="whitespace-nowrap overflow-hidden overflow-ellipsis">
                {row.customsState?.sentTo && (
                  <Badge color={DeclarationFlowBackgroundColors[row.customsState?.flow ?? 'POST']}>
                    {row.customsState?.flow?.toLowerCase() || ''}
                    {row.customsState.sentTo || ''}
                  </Badge>
                )}
              </div>
            ),
          },
          {
            name: 'Intrinsic value',
            cell: (row: Declaration) =>
              row.consignment?.intrinsicValue?.amount && row.consignment?.intrinsicValue?.currencyCode
                ? `${row.consignment.intrinsicValue.amount} ${row.consignment.intrinsicValue.currencyCode}`
                : '',
          },
        ]
      : []),
    {
      name: 'Gross Mass',
      cell: (row) => {
        return isHighValueDeclaration(row?.generalInfo?.group) ? row.goodShipmentGlobal?.totalGrossMass || 0 : row?.consignment?.grossMass || 0;
      },
    },
    {
      name: 'Dest',
      cell: (row) => row.stakeholders.importer.address.country,
    },
    {
      name: 'Exporter',
      cell: (row) => row.stakeholders.exporter.name,
    },
    ...(filters.group === 'H1'
      ? [
          ...(!settings?.isForwarder
            ? [
                {
                  name: 'Registration Number',
                  cell: (row: Declaration) => row.otherDataElements?.registrationNumber,
                },
              ]
            : []),
        ]
      : [
          {
            name: 'Reference UCR',
            cell: (row: Declaration) => row.goodsShipmentItems?.[0]?.referenceNumberUCR,
          },
        ]),
    {
      name: 'LRN',
      cell: (row, rowIdx) => <span data-pw={`cell-lrn-${rowIdx}`}>{row.messageInfo.LRN}</span>,
    },
    {
      name: 'Date',
      selector: 'updatedAt',
      cell: (row) => moment(row.updatedAt).format(EORIGIN_DATE_FORMAT_WITH_TIME),
      sortable: true,
      width: '160px',
    },
    {
      name: 'AWB',
      cell: (row) => row.messageInfo.transport,
    },
    {
      name: 'Actions',
      sticky: 'right',
      width: '160px',
      cell: (row, rowIdx) => (
        <TableActions
          actions={settings.isForwarder ? commonActions(row) : [...highValuesActions(row), ...commonActions(row), ...lowValuesActions(row)]}
          secondaryActions={[]}
          onClick={() => onSelectedRow(row)}
          disableAfterClick={true}
          data-pw={rowIdx}
        />
      ),
    },
  ];

  return buildColumns();
};
